import type { Brand } from './brand';
import { assertSafeInteger, toExactBigInt } from './integers';

/**
 * Fixed-point arithmetic backed by `bigint`.
 *
 * Idle games multiply small base values by very large percentage bonuses. A
 * level 10 Prismatic ship alone is +16896%, and later systems (artifacts, pets,
 * skins, map scaling) multiply on top of that. IEEE-754 `number` silently loses
 * integer precision past 2^53, which would make the client and the server
 * disagree about damage — an unacceptable outcome for a server-authoritative
 * economy.
 *
 * Every gameplay quantity that can grow without a hard cap is stored as a
 * `Fixed`: an integer counted in units of 1 / {@link FIXED_POINT_SCALE}.
 */
export type Fixed = Brand<bigint, 'Fixed'>;

/** 4 decimal places. Chosen once; changing it is a breaking wire change. */
export const FIXED_POINT_SCALE = 10_000n;

/** Number of decimal digits represented by {@link FIXED_POINT_SCALE}. */
export const FIXED_POINT_DECIMALS = 4;

/** Basis points denominator. 10000 bps = 100%. */
export const BPS_DENOMINATOR = 10_000;

const DECIMAL_PATTERN = /^-?\d+(\.\d+)?$/;

/** Wraps a raw scaled integer. Use only when the value is already scaled. */
export function fixedFromScaled(scaled: bigint): Fixed {
  return scaled as Fixed;
}

/** Unwraps to the raw scaled integer, for storage or serialization. */
export function fixedToScaled(value: Fixed): bigint {
  return value as bigint;
}

/**
 * Builds a `Fixed` from a whole number.
 *
 * A fractional or unsafe input is rejected, never truncated: silently turning
 * `1.9` into `1` would make the server and the client disagree about a value
 * neither of them flagged as wrong.
 */
export function fixedFromInteger(value: number | bigint): Fixed {
  return fixedFromScaled(toExactBigInt(value, 'fixedFromInteger value') * FIXED_POINT_SCALE);
}

/**
 * Builds a `Fixed` from a decimal string such as `'12.5'`.
 *
 * A string is used instead of a `number` on purpose: it keeps configuration
 * files and wire payloads free of float round-trips. Extra decimals beyond
 * {@link FIXED_POINT_DECIMALS} are truncated towards zero, never rounded, so
 * the operation is deterministic on every runtime.
 */
export function fixedFromDecimalString(value: string): Fixed {
  if (!DECIMAL_PATTERN.test(value)) {
    throw new RangeError(`Invalid decimal literal for Fixed: ${value}`);
  }
  const negative = value.startsWith('-');
  const unsigned = negative ? value.slice(1) : value;
  const [whole = '0', fraction = ''] = unsigned.split('.');
  const paddedFraction = fraction.padEnd(FIXED_POINT_DECIMALS, '0').slice(0, FIXED_POINT_DECIMALS);
  const magnitude = BigInt(whole) * FIXED_POINT_SCALE + BigInt(paddedFraction || '0');
  return fixedFromScaled(negative ? -magnitude : magnitude);
}

/** Renders a `Fixed` as a plain decimal string, truncated to `decimals`. */
export function fixedToDecimalString(value: Fixed, decimals = 2): string {
  assertSafeInteger(decimals, 'decimals');
  if (decimals < 0 || decimals > FIXED_POINT_DECIMALS) {
    throw new RangeError(`decimals must be between 0 and ${FIXED_POINT_DECIMALS}`);
  }
  const scaled = fixedToScaled(value);
  const negative = scaled < 0n;
  const magnitude = negative ? -scaled : scaled;
  const whole = magnitude / FIXED_POINT_SCALE;
  const sign = negative ? '-' : '';
  if (decimals === 0) {
    return `${sign}${whole}`;
  }
  const fraction = (magnitude % FIXED_POINT_SCALE)
    .toString()
    .padStart(FIXED_POINT_DECIMALS, '0')
    .slice(0, decimals);
  return `${sign}${whole}.${fraction}`;
}

export function fixedAdd(a: Fixed, b: Fixed): Fixed {
  return fixedFromScaled(fixedToScaled(a) + fixedToScaled(b));
}

export function fixedSubtract(a: Fixed, b: Fixed): Fixed {
  return fixedFromScaled(fixedToScaled(a) - fixedToScaled(b));
}

/** Multiplies two fixed-point values, truncating towards zero. */
export function fixedMultiply(a: Fixed, b: Fixed): Fixed {
  return fixedFromScaled((fixedToScaled(a) * fixedToScaled(b)) / FIXED_POINT_SCALE);
}

/** Multiplies by a whole number without any precision loss. */
export function fixedScaleByInteger(value: Fixed, factor: number | bigint): Fixed {
  return fixedFromScaled(
    fixedToScaled(value) * toExactBigInt(factor, 'fixedScaleByInteger factor'),
  );
}

/**
 * Applies a percentage bonus expressed in basis points.
 *
 * `applyBonusBps(x, 1500)` returns `x * 1.15`. Bonuses are additive in basis
 * points before being applied once, which is what {@link BPS_DENOMINATOR}
 * makes explicit. Whether a given bonus family stacks additively or
 * multiplicatively is a balance decision and is NOT settled here.
 */
export function applyBonusBps(value: Fixed, bonusBps: number): Fixed {
  assertSafeInteger(bonusBps, 'bonusBps');
  const multiplier = BigInt(BPS_DENOMINATOR + bonusBps);
  return fixedFromScaled((fixedToScaled(value) * multiplier) / BigInt(BPS_DENOMINATOR));
}

/** Takes `bonusBps` basis points OF a value (not value + bonus). */
export function portionBps(value: Fixed, bps: number): Fixed {
  assertSafeInteger(bps, 'bps');
  return fixedFromScaled((fixedToScaled(value) * BigInt(bps)) / BigInt(BPS_DENOMINATOR));
}

export const FIXED_ZERO: Fixed = fixedFromScaled(0n);
